/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * ChangHeon Yoo (chyoo@rsupport.com)
 *
 * @description
 *
 * @created at Wed Apr 03 2024
 **/

import { timer } from "rxjs";

import { _T } from "@rfice/language";

import { useStompClient } from "@/frameworks/libraries/network";
import { EventID, POPOVER_ID, ZIndex } from "@/frameworks/core/commons";
import { useConfirm, usePopover } from "@/frameworks/core/hooks";
import { usePersistActions, usePersistStore } from "@/frameworks/core/persistence";
import { useEventHandlers } from "@rfice/eventhandler";
import {
	GroupJoinRequestPopup,
	GroupNoticePopup,
	GroupJoined,
	SpaceJoined,
	SpaceNoticePopup,
	SpaceJoinRequestPopup,
	DeletedSpaceNoticePopup,
	GuestBookMyRoom,
	OpenWorldBlockedUserParticipated
} from "../../BackgroundUI";

export default function BackgroundUIProvider() {
	const popover = usePopover();
	const { spaces } = usePersistActions();
	const { confirm } = useConfirm();
	const { unity } = usePersistActions();

	const onCloseBackgroundPopup = () => {
		popover.hide(POPOVER_ID.BACKGROUND_UI_POPUP);
	};

	// 강제 퇴장 1회성 UI 출력용
	useEventHandlers(
		{
			[EventID.onEnterRoom]: async ({ spaceID }: IPC.Spaces.EnterSpace) => {
				const state = usePersistStore.getState();

				if (state.space.is_force_kick_out) {
					if (spaceID === state.space.is_force_kick_out.spaceID) {
						switch (state.space.is_force_kick_out.cause) {
							case "myRoomEditObjectApplied": {
								confirm({
									title: _T("마이룸 변경 안내"),
									descriptions: _T("마이룸 정보가 변경되어 마이룸에 재접속되었습니다."),
									useOnlyConfirm: true,
									descriptionAlign: "center",
									descriptionWidth: "100%"
								});
								break;
							}
							case "myRoomUpdated": {
								confirm({
									title: _T("마이룸 변경 안내"),
									descriptions: _T("마이룸 정보가 변경되어 마이룸에 재접속되었습니다."),
									useOnlyConfirm: true,
									descriptionAlign: "center",
									descriptionWidth: "100%"
								});
								break;
							}
							default: {
								confirm({
									title: _T("스페이스 변경 안내"),
									descriptions: _T("스페이스 정보가 변경되어 스페이스에 재접속되었습니다."),
									useOnlyConfirm: true,
									descriptionAlign: "center",
									descriptionWidth: "100%"
								});
								break;
							}
						}
					}
					spaces.reset_force_kick_out();
				}
			}
		},
		[]
	);

	useStompClient({
		onMessageReceived(msg) {
			switch (msg.type) {
				case "spaceInvited": {
					const payload = msg.payload as PushServices.Space.SpaceInvited.Data;
					popover.show({
						popoverID: POPOVER_ID.BACKGROUND_UI_POPUP,
						content: <SpaceNoticePopup spaceInfo={payload} onClose={onCloseBackgroundPopup} />,
						anchorEl: document.querySelector("header"),
						shouldHideOnClickOutside: false,
						position: {
							x: document.documentElement.clientWidth / 2 - 270,
							y: 0
						},
						zIndex: ZIndex.GNB + 1
					});
					timer(5000).subscribe(() => {
						onCloseBackgroundPopup();
					});
					break;
				}
				case "groupInvited": {
					const payload = msg.payload as PushServices.Group.GroupInvited.Data;
					popover.show({
						popoverID: POPOVER_ID.BACKGROUND_UI_POPUP,
						content: <GroupNoticePopup groupInfo={payload} onClose={onCloseBackgroundPopup} />,
						anchorEl: document.querySelector("header"),
						shouldHideOnClickOutside: false,
						position: {
							x: document.documentElement.clientWidth / 2 - 270,
							y: 0
						},
						zIndex: ZIndex.GNB + 1
					});
					timer(5000).subscribe(() => {
						onCloseBackgroundPopup();
					});
					break;
				}
				case "spaceDeleted": {
					const payload = msg.payload as PushServices.Space.SpaceDeleted.Data;

					//스페이스 삭제 시 삭제한 사람(관리자)에게는 팝업 노출 필요 없음
					if (payload.deletedBy === usePersistActions().users.me.id) {
						return;
					}

					popover.show({
						popoverID: POPOVER_ID.BACKGROUND_UI_POPUP,
						content: (
							<DeletedSpaceNoticePopup spaceInfo={payload.spaceInfo} onClose={onCloseBackgroundPopup} />
						),
						anchorEl: document.querySelector("header"),
						shouldHideOnClickOutside: false,
						position: {
							x: document.documentElement.clientWidth / 2 - 270,
							y: 0
						},
						zIndex: ZIndex.GNB + 1
					});
					break;
				}
				case "spaceJoinRequest": {
					const payload = msg.payload as PushServices.Space.SpaceJoinRequest.Data;

					popover.show({
						popoverID: POPOVER_ID.BACKGROUND_UI_POPUP,
						content: (
							<SpaceJoinRequestPopup
								spaceInfo={payload.spaceInfo}
								userInfo={payload.userInfo}
								onClose={onCloseBackgroundPopup}
							/>
						),
						anchorEl: document.querySelector("header"),
						shouldHideOnClickOutside: false,
						position: {
							x: document.documentElement.clientWidth / 2 - 270,
							y: 0
						},
						zIndex: ZIndex.GNB + 1
					});
					break;
				}
				case "groupJoinRequest": {
					const payload = msg.payload as PushServices.Group.GroupJoinRequest.Data;
					popover.show({
						popoverID: POPOVER_ID.BACKGROUND_UI_POPUP,
						content: (
							<GroupJoinRequestPopup
								groupInfo={payload.groupInfo}
								userInfo={payload.userInfo}
								onClose={onCloseBackgroundPopup}
							/>
						),
						anchorEl: document.querySelector("header"),
						shouldHideOnClickOutside: false,
						position: {
							x: document.documentElement.clientWidth / 2 - 270,
							y: 0
						},
						zIndex: ZIndex.GNB + 1
					});
					break;
				}
				case "groupJoined": {
					const payload = msg.payload as PushServices.Group.GroupJoined.Data;
					popover.show({
						popoverID: POPOVER_ID.BACKGROUND_UI_POPUP,
						content: <GroupJoined groupInfo={payload.groupInfo} onClose={onCloseBackgroundPopup} />,
						anchorEl: document.querySelector("header"),
						shouldHideOnClickOutside: false,
						position: {
							x: document.documentElement.clientWidth / 2 - 270,
							y: 0
						},
						zIndex: ZIndex.GNB + 1
					});
					timer(5000).subscribe(() => {
						onCloseBackgroundPopup();
					});
					break;
				}
				case "spaceJoined": {
					const payload = msg.payload as PushServices.Space.SpaceJoined.Data;
					popover.show({
						popoverID: POPOVER_ID.BACKGROUND_UI_POPUP,
						content: <SpaceJoined spaceInfo={payload.spaceInfo} onClose={onCloseBackgroundPopup} />,
						anchorEl: document.querySelector("header"),
						shouldHideOnClickOutside: false,
						position: {
							x: document.documentElement.clientWidth / 2 - 270,
							y: 0
						},
						zIndex: ZIndex.GNB + 1
					});
					timer(5000).subscribe(() => {
						onCloseBackgroundPopup();
					});
					break;
				}
				case "guestBookPosted": {
					const payload = msg.payload as PushServices.Space.GuestBookPosted.Data;
					popover.show({
						popoverID: POPOVER_ID.BACKGROUND_UI_POPUP,
						content: (
							<GuestBookMyRoom
								readKey={payload.readKey}
								spaceInfo={payload.spaceInfo}
								userInfo={payload.userInfo}
								onClose={onCloseBackgroundPopup}
							/>
						),
						anchorEl: document.querySelector("header"),
						shouldHideOnClickOutside: false,
						position: {
							x: document.documentElement.clientWidth / 2 - 270,
							y: 0
						},
						zIndex: ZIndex.GNB + 1
					});
					break;
				}
				case "openWorldBlockedUserParticipated": {
					const payload = msg.payload as PushServices.Space.OpenWorldBlockedUserParticipated.Data;
					popover.show({
						popoverID: POPOVER_ID.BACKGROUND_UI_POPUP,
						content: (
							<OpenWorldBlockedUserParticipated
								spaceInfo={payload.spaceInfo}
								onClose={onCloseBackgroundPopup}
							/>
						),
						anchorEl: document.querySelector("header"),
						shouldHideOnClickOutside: false,
						position: {
							x: document.documentElement.clientWidth / 2 - 270,
							y: 0
						},
						zIndex: ZIndex.GNB + 1
					});
					break;
				}
				case "myRoomEditObjectApplied": {
					const { myRoomId } = msg.payload as PushServices.Space.MyRoomEditObjectApplied.Data;
					const me = usePersistActions().users.me;

					// 내 방에서 발생한 이벤트가 아니면 강제 퇴장 처리
					if (myRoomId !== me.myRoomId) {
						spaces.force_kick_out(myRoomId, "MY_ROOM", "myRoomEditObjectApplied");
					}
					break;
				}

				case "myRoomUpdated": {
					const currentSpace = unity.get_my_current_space();
					const payload = msg.payload as PushServices.Space.MyRoomUpdated.Data;

					if ((currentSpace as SwaggerModels.UserSpaceResponseVO)?.spaceId === payload.id) {
						spaces.force_kick_out(payload.id, "MY_ROOM", "myRoomUpdated");
					}
					break;
				}
			}
		}
	});

	return null;
}
